import { Button, Card, Flex, Heading, Icon, Image, Link, Text } from '@chakra-ui/react';
import { useResource, useResources } from 'datx-jsonapi-react';
import { useTranslation } from 'react-i18next';

import WebIcon from '@/assets/icons/ic-black-web.svg';
import GithubIcon from '@/assets/icons/ic-github.svg';
import OrchidIcon from '@/assets/icons/ic-orcid.svg';
import XIcon from '@/assets/icons/ic-share-x-circle.svg';
import GoogleIcon from '@/assets/icons/ic-social-google.svg';
import LinkedinIcon from '@/assets/icons/ic-social-linkedin.svg';

import { formatInstitutionLine } from '@/components/Speaker/utils';
import { GeneralMarkdown } from '@/components/ui/shared/markdowns/GeneralMarkdown/GeneralMarkdown';
import { SlugIdParam, useQueryParams } from '@/hooks/useQueryParams';
import useSession from '@/hooks/useSession';
import { queryProfileInstitutions } from '@/queries/profile-instututions';
import { queryProfileStatistics } from '@/queries/profile-statistics';
import { queryProfileKeywords, queryThinProfile } from '@/queries/thin-profiles';

export const Header = () => {
  const { thinProfile } = useSession();
  const { id } = useQueryParams<{ id: string }>({
    id: SlugIdParam,
  });
  const { t } = useTranslation();

  const { data: profile } = useResource(queryThinProfile(id));
  const { data: keywords } = useResources(queryProfileKeywords(id));
  const { data: institutions } = useResources(queryProfileInstitutions(id));
  const { data: profileStatistics } = useResource(queryProfileStatistics(id));

  const isMyProfile = id === thinProfile?.id;

  if (!profile) return null;

  return (
    <Card py={{ base: 6, md: 10, lg: 12 }} px={{ base: 6, md: 10, lg: 20 }} mt={10}>
      <Flex flexDirection={{ base: 'column', md: 'row' }}>
        <Image
          alignSelf={{ base: 'center', md: 'flex-start' }}
          mr={{ base: 0, md: 10 }}
          h="214px"
          w="214px"
          borderRadius="50%"
          src={profile.squareAvatar}
          alt="profile picture"
        />
        <Flex w="full" mt={{ base: 8, md: 0 }} flexDirection="column">
          <Flex justifyContent="space-between" flexDirection={{ base: 'column', lg: 'row' }}>
            <Heading size="lg">
              {profile.firstName} {profile.lastName}
            </Heading>
            {isMyProfile && (
              <Link href="/my-account/profile">
                <Button mt={{ base: 4, lg: 0 }} alignSelf="flex-start" colorScheme="black" size="sm">
                  {t('publicSpeakerPage.editProfileButtonLabel')}
                </Button>
              </Link>
            )}
          </Flex>
          {institutions?.length > 0 && <Text mt={3}>{formatInstitutionLine(institutions[0])}</Text>}
          {keywords?.length !== 0 && (
            <Flex flexWrap="wrap" h={7} overflow="hidden" mt={4} gap={1}>
              {keywords?.map(({ name }) => (
                <Text
                  fontSize="sm"
                  noOfLines={1}
                  borderColor="black"
                  borderWidth="1px"
                  px="2"
                  py="2px"
                  borderRadius="100px"
                  key={name}
                >
                  {name}
                </Text>
              ))}
            </Flex>
          )}
          <Flex alignItems="center" flexWrap="wrap" mt={4} gap="4">
            {profile?.emailPublic && profile?.email && (
              <Link href={`mailto:${profile?.email}`}>
                <Button size="md" colorScheme="black" alignSelf="flex-start">
                  {t('publicSpeakerPage.emailButtonLabel')}
                </Button>
              </Link>
            )}
            {profile?.website && (
              <Link target="_blank" href={profile?.website}>
                <Icon mt={2} w="32px" h="32px" as={WebIcon} />
              </Link>
            )}
            {profile?.linkedin && (
              <Link target="_blank" href={profile.linkedin}>
                <Icon mt={2} w="32px" h="32px" as={LinkedinIcon} />
              </Link>
            )}
            {profile?.orchid && (
              <Link target="_blank" href={profile?.orchid}>
                <Icon mt={2} w="32px" h="32px" as={OrchidIcon} />
              </Link>
            )}
            {profile?.googleScholar && (
              <Link target="_blank" href={profile?.googleScholar}>
                <Icon mt={2} w="32px" h="32px" as={GoogleIcon} />
              </Link>
            )}
            {profile?.github && (
              <Link target="_blank" href={profile?.github}>
                <Icon mt={2} w="32px" h="32px" as={GithubIcon} />
              </Link>
            )}
            {profile?.twitter && (
              <Link target="_blank" href={profile?.twitter}>
                <Icon mt={2} w="32px" h="32px" as={XIcon} />
              </Link>
            )}
          </Flex>
        </Flex>
      </Flex>
      {profileStatistics?.lecturesCount !== 0 && (
        <Flex alignItems="flex-start" my={8}>
          <Flex alignItems="center" justifyContent="center" flexDirection="column" flex={1}>
            <Heading fontSize="xl">{profileStatistics?.lecturesCount}</Heading>
            <Text mt={2}>{t('publicSpeakerPage.numbersCounting.presentations')}</Text>
          </Flex>
          {profileStatistics?.videoStartedCount !== 0 && (
            <Flex alignItems="center" justifyContent="center" flexDirection="column" flex={1}>
              <Heading fontSize="xl">{profileStatistics?.videoStartedCount}</Heading>
              <Text align="center" mt={2}>
                {t('publicSpeakerPage.numbersCounting.videoViews')}
              </Text>
            </Flex>
          )}
          {profileStatistics?.citationsCount !== 0 && (
            <Flex alignItems="center" justifyContent="center" flexDirection="column" flex={1}>
              <Heading fontSize="xl">{profileStatistics?.citationsCount}</Heading>
              <Text mt={2}>{t('publicSpeakerPage.numbersCounting.citations')}</Text>
            </Flex>
          )}
        </Flex>
      )}
      {profile?.shortBio && (
        <Flex flexDirection="column">
          <Heading mb={2} fontSize="sm" color="gray.600">
            {t('publicSpeakerPage.shortBio')}
          </Heading>
          <Flex flexDir="column" mt={2} maxH="250px" w="full" overflowY="auto">
            <GeneralMarkdown escapeHtml={true} source={profile?.shortBio} />
          </Flex>
        </Flex>
      )}
      {institutions?.length > 1 && (
        <Flex mt={8} flexDirection="column">
          <Heading mb={2} fontSize="sm" color="gray.600">
            {t('publicSpeakerPage.otherInstitutionalAffiliations')}
          </Heading>
          <Flex flexDirection="column">
            {institutions?.slice(1).map((inst) => (
              <Text key={inst.id}>{formatInstitutionLine(inst)}</Text>
            ))}
          </Flex>
        </Flex>
      )}
    </Card>
  );
};
